import type {
    CarrierShip,
    Invoice,
    Order,
    OrderVo,
    ReceivePayment,
    Refund,
    Ship,
    ReceivePaymentVo,
    RefundPaddingParams,
    Page
} from "@zhihaoscm/entity";
import { orderService } from "@/api/services/orderService";
import { RECEIVE_STATE } from "@zhihaoscm/meta/src/receivePayment";
import { refundService } from "@/api/services/refundService";
import { DEFAULT_VALUE } from "@/config";
import { receivePaymentService } from "@/api/services/receivePaymentService";
import { tradeInvoiceService } from "@/api/services/tradeInvoiceService";
import { ORDER_TYPE, BARGAIN_END_FLAG } from "@zhihaoscm/meta";

/** React 页面使用的最小可变值容器，保留旧模块导出的 value 访问契约。 */
const createValue = <T>(value: T) => ({ value });

/** 订单Vo */
export const orderVo = createValue<OrderVo>({} as OrderVo);

/** 设置订单Vo */
export const setOrderVo = async (id: string) => {
    orderVo.value = await orderService.getVo(id);
    await getReceivePaymentData({ page: 1, size: 5, orderId: orderVo.value.order.id });
    await getRefundData({ page: 1, size: 5, orderId: orderVo.value.order.id });
    await getInvoiceData({ page: 1, size: 100, orderId: orderVo.value.order.id });
};

/** 计算属性 */
export const order = {
    get value(): Order {
        return orderVo.value.order || ({} as Order);
    }
};

// 退款列表数据
export const refundList = createValue<Refund[]>([]);
// 退款总数据
export const refundTotalData = createValue<number>(0);

/** 获取退款数据 */
export const getRefundData = async (params: { page: number; size: number; orderId: string }) => {
    const data = await refundService.paging(params as RefundPaddingParams);
    refundList.value = data.list;
    refundTotalData.value = data.total;
};

/** 收款数据 */
export const receivePaymentList = createValue<ReceivePayment[]>([]);
/** 收款Vo数据 */
export const receivePaymentVoList = createValue<ReceivePaymentVo[]>([]);
// 收款总数据
export const receivePaymentTotalData = createValue<number>(0);
// 获取收款数据
export const getReceivePaymentData = async (params: { page: number; size: number; orderId: string }) => {
    // BaseService 的列表泛型未能从该历史服务声明中保留，按接口真实返回的 VO 分页恢复类型。
    const data = (await receivePaymentService.paging(params)) as Page<ReceivePaymentVo>;
    // 收款Vo数据
    receivePaymentVoList.value = data.list;
    // 收款数据
    receivePaymentList.value = data.list.map((item: ReceivePaymentVo) => {
        return item.receivePayment;
    });
    receivePaymentTotalData.value = data.total;
};

// 是否展示支付剩余时间
export const isShowRestTime = (orderVo: OrderVo) => {
    // 如果目前付款记录内有待支付费用 就展示支付剩余时间
    if (!orderVo.receivePayments?.length) return false;
    // 存在待支付的付款记录  并且  存在付款截止时间   （没有付款截止时间为 支付 不限时长）
    return orderVo.receivePayments.find(
        (item: ReceivePayment) => item.state === RECEIVE_STATE.PENDING_PAYMENT.code && item.payEndTime
    );
};

// 是否展示助力倒计时
export const isShowHelpRestTime = (orderVo: OrderVo) => {
    return (
        orderVo.order.orderType === ORDER_TYPE.PICKING_UP_MISSING_PRODUCTS.code &&
        orderVo.bargainEndFlag === BARGAIN_END_FLAG.NO.code
    );
};
// 订单中支付剩余时间最少的那条收款记录的费用类型
export let minPendingPaymentFeeState: number;

// 支付剩余时间倒计时 格式化剩余时间为 "时:分:秒"
export const formatRemainingTime = (receivePayments?: ReceivePayment[], callback?: () => void) => {
    if (!receivePayments?.length) {
        return {
            formattedTime: DEFAULT_VALUE,
            isEnd: true
        };
    }
    const pendingPayments = receivePayments.filter(
        (item: ReceivePayment) => item.state === RECEIVE_STATE.PENDING_PAYMENT.code
    );
    let minPendingPayment = pendingPayments[0];
    // 找到支付剩余时间最少的那一条收款记录
    pendingPayments.forEach((item: ReceivePayment) => {
        if (new Date(minPendingPayment.payEndTime).getTime() > new Date(item.payEndTime).getTime()) {
            minPendingPayment = item;
        }
    });
    minPendingPaymentFeeState = minPendingPayment.paymentType;
    const endTime = new Date(minPendingPayment.payEndTime).getTime();
    const now = Date.now();
    const remaining = endTime - now;

    if (remaining <= 0) {
        // 当前时间超过结束时间1s，认为是结束，关闭倒计时
        const isEnd = now - endTime >= 1000 ? true : false;
        // 当前时间超过结束时间1s内，执行回调函数用于刷新页面
        if (callback && !isEnd) callback();
        return {
            formattedTime: "00:00:00",
            isEnd
        };
    }

    const days = Math.floor(remaining / 86400000);
    const hours = Math.floor((remaining % 86400000) / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    if (days > 0) {
        return {
            formattedTime: `${days}天${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
            isEnd: false
        };
    }

    return {
        formattedTime: `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
        isEnd: false
    };
};

// 助力剩余倒计时
export const formatHelpRemainingTime = (bargainEndTime: string) => {
    const endTime = new Date(bargainEndTime).getTime();
    const now = Date.now();
    const remaining = endTime - now;

    if (remaining <= 0) {
        return DEFAULT_VALUE;
    }

    const days = Math.floor(remaining / 86400000);
    const hours = Math.floor((remaining % 86400000) / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    if (days > 0) {
        return `${days}天${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    } else {
        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    }
};

// 获取船舶名称
export const getShipInfoName = (shipInfo?: CarrierShip | Ship, shipId?: string) => {
    if (!shipInfo || (!shipInfo.cnname && !shipInfo.name && !shipId)) return DEFAULT_VALUE;
    const name = shipInfo.cnname || shipInfo.name;
    return name ? `${name}/${shipId}` : shipId;
};

/** 发票信息列表数据 */
export const invoiceList = createValue<Invoice[]>([]);
/** 发票总数据 */
export const invoiceTotalData = createValue<number>(0);
export const getInvoiceData = async (params: { page: number; size: number; orderId: string }) => {
    const data = await tradeInvoiceService.paging(params);
    invoiceList.value = data.list;
    invoiceTotalData.value = data.total;
};
